import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink } from '../../components/site/Pill';
import { Reveal } from '../../components/site/Reveal';
import { INTERIORS, PILLARS, STATS, TEAM } from '../../lib/site';

const i = (n: number) => ({ '--i': n }) as React.CSSProperties;

export default function SiteAbout() {
  return (
    <SiteLayout>
      <section className="bg-cream">
        <div className="site-narrow page-head section-b">
          <p className="rise eyebrow" style={i(0)}>About Lotus Living</p>
          <h1 className="rise h-page mt-4 max-w-[18ch] text-ink-900" style={i(1)}>Building high-quality homes just for renters</h1>
          <div className="rise mt-8 grid gap-6 md:mt-12 md:grid-cols-2 md:gap-16 lg:gap-24" style={i(2)}>
            <p className="lede text-ink-700">A boutique, family-owned British developer with deep roots in London. We don't just build apartments. We build homes.</p>
            <p className="prose-site text-ink-600">Modern, comfortable, sustainable build-to-rent homes, finished with the touches that make people feel at ease from day one, and looked after by us for as long as they stay.</p>
          </div>
        </div>
      </section>

      <section className="site-narrow section-y grid items-center gap-10 md:grid-cols-2 md:gap-14 lg:gap-24">
        <Reveal className="order-2 md:order-1">
          <p className="eyebrow">Our founder</p>
          <h2 className="h-section mt-4 text-ink-900">Rajiv Nehru, CEO</h2>
          <div className="prose-site mt-6 space-y-5 text-ink-600">
            <p>More than two decades across property management, contracting and development.</p>
            <p>Rajiv has grown Lotus Living into a Build-to-Rent platform of around 1,000 homes worth £300 million, with an ambition of 10,000 homes worth £3.5 billion, built on relationships and communities people are proud to come home to.</p>
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-ink-200 pt-8 sm:gap-8">
            {STATS.map((s) => (
              <div key={s.label}><dt className="h-stat text-ink-900">{s.value}</dt><dd className="mt-2 text-[13px] text-ink-500">{s.label}</dd></div>
            ))}
          </dl>
        </Reveal>
        <Reveal variant="image" className="order-1 overflow-hidden rounded-[10px] md:order-2">
          <img src={INTERIORS.welcomeHamper} alt="Welcome hamper at Drake House" className="aspect-[4/3] w-full object-cover sm:aspect-[4/5]" />
        </Reveal>
      </section>

      <section className="border-t border-ink-100">
        <div className="site-narrow section-y">
          <Reveal as="h2" className="h-section max-w-[20ch] text-ink-900">We don't just build apartments. We build homes.</Reveal>
          <div className="mt-10 grid gap-x-16 gap-y-10 md:mt-14 md:grid-cols-2 md:gap-y-12">
            {PILLARS.map((p, n) => (
              <Reveal key={p.title} delay={(n % 2) * 120} className="border-t border-ink-200 pt-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-bronze-500">0{n + 1}</div>
                <h3 className="h-card mt-3 text-ink-900">{p.title}</h3>
                <p className="mt-3 text-[15px] leading-[1.75] text-ink-600">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink-900 text-white">
        <img src={INTERIORS.livingArea} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" loading="lazy" />
        <div className="site-narrow section-y relative z-10 grid gap-8 md:grid-cols-2 md:gap-16">
          <Reveal>
            <p className="eyebrow !text-bronze-300">Build to Rent</p>
            <h2 className="h-section mt-4">Grow, Live and Thrive</h2>
          </Reveal>
          <Reveal delay={120} className="prose-site space-y-5 text-white/80 md:pt-2">
            <p>Whether it's your first rental or your tenth, you'll find our communities warm, welcoming and inclusive. We're more than a landlord: we build places where residents live, grow and thrive.</p>
            <PillLink to="/rent" variant="white" className="!mt-8">Find your new home</PillLink>
          </Reveal>
        </div>
      </section>

      <section className="site-narrow section-y">
        <Reveal>
          <p className="eyebrow">The team</p>
          <h2 className="h-section mt-4 text-ink-900">A friendly, approachable team</h2>
        </Reveal>
        <ul className="mt-10 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 lg:gap-y-8">
          {TEAM.map(([name, role], n) => (
            <Reveal as="li" key={name} delay={(n % 4) * 80} className="flex items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-bronze-100 font-display text-[19px] font-medium text-bronze-700">{name.split(' ').map((w) => w[0]).join('')}</span>
              <span className="min-w-0"><span className="block text-[15px] font-medium text-ink-900">{name}</span><span className="block text-[13px] text-ink-500">{role}</span></span>
            </Reveal>
          ))}
        </ul>
      </section>
    </SiteLayout>
  );
}
